import { Avatar, AvatarSize, FlexBox } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { User } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';

import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import styles from './userProfile.module.scss';

type UserProfileProps = {
  /** The user object. */
  user?: User;
  /** The label when the user is undefined. */
  unknownLabel?: string;
  /** The size of the avatar. */
  avatarSize?: AvatarSize;
  /** The optional description. */
  description?: string;
  /** Whether or to clamp the name to a single line and truncate accordingly. */
  truncateName?: boolean;
  /** The class name of the name element. */
  nameClassName?: string;
  /**
   * Whether or to clamp the description to a single line and truncate
   * accordingly.
   */
  truncateDescription?: boolean;
  /** The class name of the description element. */
  descriptionClassName?: string;
} & PropsWithClassName;

/**
 * Represents the component for displaying the user profile which includes an
 * avatar and a display name inferred from the provided user object.
 */
export const UserProfile = ({
  avatarSize = '24',
  className,
  description,
  descriptionClassName,
  nameClassName,
  truncateDescription,
  truncateName,
  unknownLabel = 'Unknown',
  user,
}: UserProfileProps): JSX.Element => {
  return (
    <FlexBox
      className={classnames(styles.root, className)}
      alignItems="center"
      gap="250"
    >
      {avatarSize && (
        <Avatar className={styles.avatar} size={avatarSize} user={user} />
      )}
      <div className={styles.content}>
        <div
          className={classnames(styles.name, nameClassName, {
            [styles.truncate]: truncateName,
          })}
        >
          {user ? getUserDisplayName(user) : unknownLabel}
        </div>
        {description && (
          <div
            className={classnames(styles.description, descriptionClassName, {
              [styles.truncate]: truncateDescription,
            })}
          >
            {description}
          </div>
        )}
      </div>
    </FlexBox>
  );
};
